import Big from 'big.js';
import { isNotNullOrUndefined, MathRound } from '../../helper/helper';
import { AccountsReceivableLedgerType } from '../graphql-types';
import { BackendDate } from '../helper/backend-frontend-conversion.helper';
import { ARLCustomProperties } from './accounts-receivable-ledger-template.entity';
import { LedgerImportDocumentTypes, ledgerImportDocumentTypesValues } from './ledger-import.entity';
import { LVPositionType } from './ava-lv-entry.entity';

export const QUANTITY_MAX_DECIMAL_PLACES = 6;

export enum AccountsReceivableLedgerTypeEnum {
    Arl = 'Arl',
    EmployeeArl = 'EmployeeArl',
    ExternalService = 'ExternalService',
    Group = 'Group',
    Other = 'Other',
    ResourceArl = 'ResourceArl',
    MaterialArl = 'MaterialArl',
    Text = 'Text',
}
export const LVPositionTypeToAccountsReceivableLedgerTypeEnum: Record<LVPositionType, AccountsReceivableLedgerTypeEnum> = {
    [LVPositionType.Text]: AccountsReceivableLedgerTypeEnum.Text,
    [LVPositionType.Title]: AccountsReceivableLedgerTypeEnum.Group,
    [LVPositionType.Position]: AccountsReceivableLedgerTypeEnum.Arl,
    [LVPositionType.Trade]: AccountsReceivableLedgerTypeEnum.Other,
}
export const AccountsReceivableLedgerTypePreventGlobalPreCharge = (type: AccountsReceivableLedgerTypeEnum) => {
    return type === AccountsReceivableLedgerTypeEnum.EmployeeArl;
}
export const availableBookingPositionTypeEnums = [
    AccountsReceivableLedgerTypeEnum.Text,
    AccountsReceivableLedgerTypeEnum.Group,
    AccountsReceivableLedgerTypeEnum.Arl,
    AccountsReceivableLedgerTypeEnum.EmployeeArl,
    AccountsReceivableLedgerTypeEnum.MaterialArl,
    AccountsReceivableLedgerTypeEnum.ResourceArl,
    AccountsReceivableLedgerTypeEnum.ExternalService,
    AccountsReceivableLedgerTypeEnum.Other,
];
export const isBookingPositionTypeEnum = (type: AccountsReceivableLedgerTypeEnum) => {
    return type !== AccountsReceivableLedgerTypeEnum.Group && type !== AccountsReceivableLedgerTypeEnum.Text;
};
export const ARLTypeEnumNames: Map<AccountsReceivableLedgerTypeEnum, string> = new Map<AccountsReceivableLedgerTypeEnum, string>([
    [AccountsReceivableLedgerTypeEnum.Text, 'Text'],
    [AccountsReceivableLedgerTypeEnum.Group, 'Titel'],
    [AccountsReceivableLedgerTypeEnum.Arl, 'Leistung'],
    [AccountsReceivableLedgerTypeEnum.ResourceArl, 'Gerät'],
    [AccountsReceivableLedgerTypeEnum.MaterialArl, 'Material'],
    [AccountsReceivableLedgerTypeEnum.EmployeeArl, 'Lohn'],
    [AccountsReceivableLedgerTypeEnum.ExternalService, 'Fremdleistung'],
    [AccountsReceivableLedgerTypeEnum.Other, 'Sonstiges'],
]);

export enum AccountsReceivableLedgerStatusEnum {
    Neu = 'NEU',
    Versandt = 'VERSANDT',
    Gebucht = 'GEBUCHT',
    Verrechnet = 'VERRECHNET',
    Storniert = 'STORNIERT',
    AngebotAkzeptiert = 'ANGEBOT_AKZEPTIERT',
}
export const AccountsReceivableLedgerStatusEnumNames: Map<AccountsReceivableLedgerStatusEnum, string> = new Map<AccountsReceivableLedgerStatusEnum, string>([
    [AccountsReceivableLedgerStatusEnum.Neu, 'Neu'],
    [AccountsReceivableLedgerStatusEnum.Versandt, 'Versandt'],
    [AccountsReceivableLedgerStatusEnum.Gebucht, 'Gebucht'],
    [AccountsReceivableLedgerStatusEnum.Verrechnet, 'bezahlt'],
    [AccountsReceivableLedgerStatusEnum.Storniert, 'Storniert'],
    [AccountsReceivableLedgerStatusEnum.AngebotAkzeptiert, 'Bestätigt'],
]);
export const CalcDiscount = (amount: number, discountPercentage: number) => financialRound(amount * ((100 - (discountPercentage || 0)) / 100));


/**
 * @deprecated only works without allowance/charges
 * @constructor
 */
export const CalcAccountsReceivableLedgerAmount = (Quantity: number, Tax: number, BaseCost: number) => {
    if (isNotNullOrUndefined(Quantity) && isNotNullOrUndefined(Tax) && isNotNullOrUndefined(BaseCost)) {
        return parseFloat((NettoCost(Quantity, BaseCost) + CalcAccountsReceivableLedgerTaxAmount(Quantity, Tax, BaseCost)).toFixed(10));
    }
    return undefined;
};
export const NettoCost = (Quantity: number, BaseCost: number) => financialRound(Quantity * BaseCost);
// export const financialRound = (number: number) => +(MathRound(Number(number + 'e+2'), true) + 'e-2');
/**
 *
 * @param number
 * @param dp Decimal places, 0 to 1e+6 inclusive
 */
export const financialRound = (number: number, dp = 2) => Big(number).round(dp, Big.roundHalfUp).toNumber();

export const financialMultiplication = (...numbers: number[]) => numbers.reduce((a, b) => a.times(b), new Big(1)).toNumber();
/**
 * @deprecated only works without allowance/charges
 * @constructor
 */
export const CalcAccountsReceivableLedgerTaxAmount = (Quantity: number, Tax: number, BaseCost: number) => financialRound(Quantity * BaseCost * (Tax / 100));
/**
 * @param ParentBaseCostAllowances must be in the correct order from leaf to root
 */
export const CalcAccountsReceivableLedgerNettoAmountWithAllowances = (
    { Quantity, Type, BaseCost, BaseCostAllowances, SurchargeOverride, FixedPrices }: Pick<AccountsReceivableLedgerEntity, 'FixedPrices' | 'Quantity' | 'Type' | 'BaseCost' | 'BaseCostAllowances' | 'SurchargeOverride'>,
    ParentBaseCostAllowances: ARLAllowance[][],
    skipAllowances = false,
) => {
    let netto = financialRound(financialMultiplication((Quantity || 0), (BaseCost || 0)));
    return _calcAccountsReceivableLedgerAmountWithAllowances({ Type, BaseCostAllowances, SurchargeOverride, FixedPrices }, ParentBaseCostAllowances, netto, skipAllowances);
}

/**
 * @param ParentBaseCostAllowances must be in the correct order from leaf to root
 */
export const CalcAccountsReceivableLedgerBruttoAmountWithAllowances = (
    { Quantity, Type, BaseCostGross, BaseCostAllowances, SurchargeOverride, FixedPrices }: Pick<AccountsReceivableLedgerEntity, 'FixedPrices' | 'Quantity' | 'Type' | 'BaseCostGross' | 'BaseCostAllowances' | 'SurchargeOverride'>,
    ParentBaseCostAllowances: ARLAllowance[][],
    skipAllowances = false,
) => {
    let gross = financialRound(financialMultiplication((Quantity || 0), (BaseCostGross || 0)));
    return _calcAccountsReceivableLedgerAmountWithAllowances({ Type, BaseCostAllowances, SurchargeOverride, FixedPrices }, ParentBaseCostAllowances, gross, skipAllowances);
}

export const _calcAccountsReceivableLedgerAmountWithAllowances = (
    { /*Type,*/ BaseCostAllowances, SurchargeOverride, FixedPrices }: Pick<AccountsReceivableLedgerEntity, 'FixedPrices' | 'Type' | 'BaseCostAllowances' | 'SurchargeOverride'>,
    ParentBaseCostAllowances: ARLAllowance[][],
    basePrice: number,
    skipAllowances = false,
    skipRounding = false,
) => {


    // D277-6018 if specific type exists all other surcharges must be ignored
    // D277-5675 if FixedPrices all other surcharges must be ignored
    if (isNotNullOrUndefined(SurchargeOverride) || FixedPrices) {
        // basePrice = financialRound(basePrice * (1 + allowanceCharges.flat().find((arl) => arl.arlType === Type).percent / 100));
        return basePrice;
    } else {
        const allowanceCharges = [BaseCostAllowances, ...ParentBaseCostAllowances];

        allowanceCharges.forEach((baseCostAllowance) => {
            // const chargesByType: ARLAllowance[] = [];
            const charges: ARLAllowance[] = [];
            const allowances: ARLAllowance[] = [];

            baseCostAllowance.forEach((ac) => {
                if (!ac.percent) {
                    return;
                }
                if (ac.arlType) {
                    // if (ac.arlType === Type) {
                    //         console.log('allowances or charges by type is not supported',{BaseCostAllowances, ParentBaseCostAllowances, basePrice, entity: { Type, BaseCostAllowances, SurchargeOverride }});
                    //         throw 'allowances or charges by type is not supported'; // diese werden für das setzen des SurchargeOverride genutzt

                        // muss auch ohne Type funktionieren damit das mit der invoiceCalculation auch funktioniert, da werden kalkulations arls hiermit errechnet
                        // if (ac.percent >= 0) {
                        //     chargesByType.push(ac);
                        // } else {
                        //     throw 'allowances by type is not supported';
                        // }
                    // }
                } else {
                    if (ac.percent > 0) {
                        charges.push(ac);
                    } else if (ac.percent < 0 && !skipAllowances) {
                        allowances.push(ac);
                    }
                }
            });
            [/*chargesByType,*/ charges, allowances].forEach((chargeAllowances) => {
                const chargeSum = chargeAllowances.reduce((p, c) => p + c.percent, 0);
                const ret = basePrice * (1 + chargeSum / 100);
                basePrice =  skipRounding ? ret : financialRound(ret);
            });
        });
    }


    return basePrice;
};

type ARLAllowance = {
    title: string;
    percent: number;
    arlType: AccountsReceivableLedgerTypeEnum | null;
};
export class AccountsReceivableLedgerEntity {
    public static readonly CostAmountDecimalPlaces = 6;
    public static readonly QuantityDecimalPlaces = 6;
    public static readonly QuantityHistoryDeltaDecimalPlaces = 6; // theoretisch egal da es als string gespeichert wird, aber so passt es zur quantity der arl
    constructor(
        public Id: number = null,
        public Date: Date = null, // - date (für Datev Format: yyyy-mm-dd) (Rechnungsdatum)
        public Amount: number = null, // - float (Gesamtbetrag) Quantity * BaseCost * (100 + Tax)
        public AccountNo: string = null, // (im Kommentar beschrieben) -> http://jira.omnia5.local/browse/DBASE-1385
        public BuCode: string = null, // - string (Buchungsschlüssel) nullable (default: 3 -> 19%) https://apps.datev.de/dnlexka/document/1008613
        public CostCategoryId: number = null, // - int (standard: 10) (kostenstellen beim steuerberater anfragen)
        public CostCategoryId2: number = null, // - int (standard: 20)
        public Tax: number = null, // - float (MWST default: 19%)
        public Information: string = null, // - string nullable (Evtl. Buchungstext für Datev)
        public CurrencyCode: string = null, // - char(3) (default: EUR)
        public InvoiceId: string = null, // - string (Rechnungsnr als sTring)
        public BookingText: string = null, // - string nullable (buchungstext evtl. Datev) max 30 Zeichen
        public PartyId: string = null, // - string (Kunden für beleg)
        public PaidAt: Date = null, // - date nullable (in Datev im format yyyy-mm-dd)
        public Iban: string = null, // - string nullable
        public DueDate: Date = null, // - date (Fälligkeitsdatum) nullable
        public CustomerName: string = null, // - string nullable
        public CustomerCity: string = null, // - string nullable

        // Dinge die man ausklappen kann im FE:
        public CostAmount: number = null, // - float nullable (Was ist das?)
        public DiscountAmount: number = null, // - float (Skontobetrag) nullable
        public InternalInvoiceId: string = null, // - string nullable (interne Rechnungsnummer)
        public VatId: string = null, // - string nullable (UmsatzsteuerId des Geschäftspartner)
        public ExchangeRate: number = null, // - float nullable (Umrechnungskurs)
        public BankCode: string = null, // - string nullable (BLZ)
        public BankAccount: string = null, // - string nullable (Bankkontonummer)
        public BankCountry: string = null, // - char 2 nullable
        public SwiftCode: string = null, // - string nullable
        public AccountName: string = null, // - Bezeichnung des Sachkontos
        public PaymentConditionsId: string = null, // - string nullable (Zahlungsbedingungsnummer)
        public DiscountPercentage: number = null, //
        public DiscountPaymentDate: Date = null, //
        public DiscountAmount2: number = null, //
        public DiscountPercentage2: number = null, //
        public DiscountPaymentDate2: Date = null, //
        public BpAccountNo: string = null, // - string nullable (default: Datev 10.000)
        public DeliveryDate: Date = null, // (wenn gesetzt, dann auch in LedgerImport setzen)
        public OrderId: string = null, // (wenn gesetzt, dann auch in LedgerImport setzen)y
        public CanBeEdited: boolean = null, // - bit default true
        public LedgerImportId: number = null, // - bigint nullable

        public PartnerId: number = null,
        public UserId: number = null,
        public CustomerId: number = null,
        public CommissionId: number = null,
        public EventId: number = null,
        public CreatedAt: Date = null,
        public UpdatedAt: Date = null,
        public DeletedAt: Date = null,

        public BaseCost: number = null,
        public Quantity: number = null,
        public QuantityTypeId: number = null,

        public Multiplier: number = null,
        public CustomProperties: ARLCustomProperties = null,
        public Status: AccountsReceivableLedgerStatusEnum = null,
        public PartnerOfficeId: number = null,
        public DocumentType: LedgerImportDocumentTypes = null,
        public GeneratedARLId: number = null,
        public AmountCalculation: any = null,
        public InheritFromChildren: boolean = null,
        public IsVisible: boolean = null,
        public ParentId: number = null,
        public ResourceId: number = null,
        public MaterialId: number = null,
        public ArlIds: number[] = null,
        public Type: AccountsReceivableLedgerTypeEnum = null,
        public Longtext: string = null,
        public ShowLongtext: boolean = null,
        public JobSpecificationId: number = null,
        public AlreadyPayed: number = null, // Bereits gezahlter amount, also das gleiche wie im Businessvolume (welche es nur für den ganzen ledgerImport gibt), wird aber nicht automatisch aktualisiert
        public RepresentsLedgerImportId: number = null, // repräsentiert einen LedgerImport, z.B.: eine Teilrechnung, welche dann in der Schlussrechnung angezeigt wird
        public IsOptional: boolean = null,
        public CrossedOut: boolean = null,
        public HideInGroupSummary: boolean = null,
        /**
         * Anteil des BaseCost, der durch Löhne entstanden ist, nur für Österreich
         */
        public SalaryBaseCost: number = null,
        /**
         * null -> hat keine item number
         * ' ' -> soll automatisch generiert werden
         * '' -> !! gibt es nicht
         * alles andere ist eine Positionsnummer
         */
        public ItemNumber: string = null,
        public IndividuallyCostCalculation: boolean = null,
        public SupplierId: number = null,
        public StartOnNewPage: boolean = null,
        public BaseCostAllowances: ARLAllowance[] = null,
        public CostAmountAllowance: number | null = null,
        public ShowQuantityHistory: boolean | null = null,
        public QuantityHistory: { date: Date; quantityDelta: number; quantityDeltaFormula: string }[] = null,
        public UseQuantityHistory: boolean | null = null,
        public LVFlEk: number | null = null,
        public LVGEK: number | null = null,
        public LVMinutes: number | null = null,
        public LVSoAntEk: number | null = null,
        public LVSoEk: number | null = null,
        public LVPartieHEur: number | null = null,
        public LVSoEkRabatt: number | null = null,
        public BaseCostGross: number = null,
        public QuantityFormula: string | null = null,
        public PriceComparisonId: number | null = null,
        public ChangedAfterSelectedFromPriceComparison: boolean | null = null,
        public Gap: { id: number, caption: string, body: string, indexInLongText: number }[] = null,
        /**
         * if not null calculation changes
         */
        public SurchargeOverride: number | null = null,
        /**
         * if true, surcharges have no effect
         */
        public FixedPrices: boolean = null,

    ) {}
    get isBookingPosition() {
        return isBookingPositionTypeEnum(this.Type);
    }

    /**
     * @deprecated richtige nummern vergeben, das ist nur für legacy arls die man nicht migrieren wollte
     */
    static readonly autogenerateItemNumber = ' ';

    /**
     * @deprecated richtige nummern vergeben, das ist nur für legacy arls die man nicht migrieren wollte
     */
    static readonly autogenerateItemNumbers = [AccountsReceivableLedgerEntity.autogenerateItemNumber, ''];

    GetBackgroundColorClass() {
        switch (this.Status) {
            case AccountsReceivableLedgerStatusEnum.Verrechnet:
                return 'app-color-invoice-verrechnet';
            case AccountsReceivableLedgerStatusEnum.Gebucht:
                if (this.DueDate < new Date()) {
                    return 'app-color-late';
                } else {
                    return 'app-color-invoice-booked';
                }
            case AccountsReceivableLedgerStatusEnum.Storniert:
                return 'app-color-invoice-storniert';
            case AccountsReceivableLedgerStatusEnum.AngebotAkzeptiert:
                return 'app-color-invoice-approved';
            default:
                return null;
        }
    }
    public static NettoCost = NettoCost;
    public get CompleteNettoCost() {
        return NettoCost(this.Quantity, this.BaseCost);
    }

    /**
     * @deprecated only works without allowance/charges
     * @constructor
     */
    public get CompleteCost() {
        return CalcAccountsReceivableLedgerAmount(this.Quantity, this.Tax, this.BaseCost);
    }

    /**
     * @deprecated only works without allowance/charges
     * @constructor
     */
    public get CompleteTaxCost() {
        if (isNotNullOrUndefined(this.Quantity) && isNotNullOrUndefined(this.Tax) && isNotNullOrUndefined(this.BaseCost)) {
            return CalcAccountsReceivableLedgerTaxAmount(this.Quantity, this.Tax, this.BaseCost);
        }
        return undefined;
    }

    public static readonly GqlFields: Array<keyof AccountsReceivableLedgerType> = [
        'accountName',
        'accountNo',
        'amount',
        'bankAccount',
        'bankCode',
        'bankCountry',
        'baseCost',
        'bookingText',
        'bpAccountNo',
        'buCode',
        'canBeEdited',
        'commissionId',
        'costAmount',
        'costCategoryId',
        'costCategoryId2',
        'createdAt',
        'currencyCode',
        'customerCity',
        'customerId',
        'customerName',
        'date',
        'deletedAt',
        'deliveryDate',
        'discountAmount',
        'discountAmount2',
        'discountPaymentDate',
        'discountPaymentDate2',
        'discountPercentage',
        'discountPercentage2',
        'dueDate',
        'eventId',
        'exchangeRate',
        'generatedDocumentId',
        'iban',
        'id',
        'information',
        'internalInvoiceId',
        'invoiceId',
        'ledgerImportId',
        'orderId',
        'paidAt',
        'partnerId',
        'partyId',
        'paymentConditionsId',
        'quantity',
        'quantityTypeId',
        'swiftCode',
        'tax',
        'updatedAt',
        'userId',
        'vatId',
        'multiplikator',
        'customProperties',
        'partnerOfficeId',
        'documentType',
        'generatedARLId',
        'status',
        'amountCalculation',
        'inheritFromChildren',
        'isVisible',
        'parentId',
        'resourceId',
        'materialId',
        'arlIds',
        'type',
        'longInformation',
        'showLongInformation',
        'jobSpecificationId',
        'payedAmount',
        'isOptional',
        'isCrossedOut',
        'lohnBaseCostPart',
        'itemNumber',
        'hideInGroupSummary',
        'supplierId',
        'startOnNewPage',
        // @ts-ignore
        'baseCostAllowances {percent, title, type}',
        'costAmountAllowance',
        // @ts-ignore
        'quantityHistories {date, quantityDelta, quantityDeltaFormula}',
        'showQuantityHistory',
        'useQuantityHistory',
        'lVFlEk',
        'lVGEK',
        'lVMinutes',
        'lVSoAntEk',
        'lVSoEk',
        'lvPartieHEur',
        'lvSoEkRabatt',
        'baseCostGross',
        'quantityFormula',
        // todo 'quantityFormulaContext'
        'priceComparisonId',
        'changedAfterSelectedFromPriceComparison',
        // @ts-ignore
        'gaps {id, caption, body, indexInLongText, deletedAt}',
        'surchargeOverride',
        'fixedPrices',
    ];
    public Clone(override: Partial<AccountsReceivableLedgerEntity> = {}): AccountsReceivableLedgerEntity {
        const attr = { ...this, ...override };
        return Object.assign(new AccountsReceivableLedgerEntity(), attr);
    }
}

export function AccountsReceivableLedgerEntityFromBackend(res: AccountsReceivableLedgerType): AccountsReceivableLedgerEntity {
    return new AccountsReceivableLedgerEntity(
        res.id,
        res.date && BackendDate(res.date),
        res.amount && res.amount / 100,
        res.accountNo,
        res.buCode,
        res.costCategoryId,
        res.costCategoryId2,
        res.tax,
        res.information,
        res.currencyCode,
        res.invoiceId,
        res.bookingText,
        res.partyId,
        res.paidAt && BackendDate(res.paidAt),
        res.iban,
        res.dueDate && BackendDate(res.dueDate),
        res.customerName,
        res.customerCity,

        res.costAmount && res.costAmount / 100,
        res.discountAmount && res.discountAmount / 100,
        res.internalInvoiceId,
        res.vatId,
        res.exchangeRate,
        res.bankCode,
        res.bankAccount,
        res.bankCountry,
        res.swiftCode,
        res.accountName,
        null, // todo einkommentieren res.paymentConditionsId,
        res.discountPercentage,
        res.discountPaymentDate && BackendDate(res.discountPaymentDate),
        res.discountAmount2 && res.discountAmount2 / 100,
        res.discountPercentage2,
        res.discountPaymentDate2 && BackendDate(res.discountPaymentDate2),
        res.bpAccountNo,
        res.deliveryDate && BackendDate(res.deliveryDate),
        res.orderId,

        res.canBeEdited,
        res.ledgerImportId,
        res.partnerId,
        res.userId,
        res.customerId,
        res.commissionId,
        res.eventId,
        res.createdAt && BackendDate(res.createdAt),
        res.updatedAt && BackendDate(res.updatedAt),
        res.deletedAt && BackendDate(res.deletedAt),
        res.baseCost / 100,
        res.quantity,
        res.quantityTypeId,
        res.multiplikator,
        res.customProperties as any,
        res.status,
        res.partnerOfficeId,
        ledgerImportDocumentTypesValues.includes(res.documentType as any) ? (res.documentType as LedgerImportDocumentTypes) : null,
        res.generatedARLId,
        res.amountCalculation,
        res.inheritFromChildren,
        res.isVisible,
        res.parentId,
        res.resourceId,
        res.materialId,
        res.arlIds,
        (res.type as unknown as AccountsReceivableLedgerTypeEnum) || AccountsReceivableLedgerTypeEnum.Arl,
        res.longInformation,
        res.showLongInformation,
        res.jobSpecificationId,
        res.payedAmount / 100,
        null, // todo einbauen
        res.isOptional,
        res.isCrossedOut,
        res.hideInGroupSummary,
        (res.lohnBaseCostPart || 0) / 100,
        res.itemNumber,
        !!(res.customProperties as ARLCustomProperties)?.individuallyCostCalculation,
        res.supplierId,
        res.startOnNewPage,
        res.baseCostAllowances
            ? res.baseCostAllowances.map((ac) => ({
                  title: ac.title,
                  arlType: ac.type as AccountsReceivableLedgerTypeEnum,
                  percent: ac.percent,
              }))
            : [],
        res.costAmountAllowance,
        res.showQuantityHistory,
        res.quantityHistories ? res.quantityHistories.map((h) => ({ date: BackendDate(h.date), quantityDelta: h.quantityDelta, quantityDeltaFormula: h.quantityDeltaFormula })) : [],
        res.useQuantityHistory,
        res.lVFlEk,
        res.lVGEK,
        res.lVMinutes,
        res.lVSoAntEk,
        res.lVSoEk,
        res.lvPartieHEur,
        res.lvSoEkRabatt,
        res.baseCostGross / 100,
        res.quantityFormula,
        res.priceComparisonId,
        res.changedAfterSelectedFromPriceComparison,
        res.gaps ? res.gaps.map((g) => ({ id: g.id, caption: g.caption, body: g.body, indexInLongText: g.indexInLongText,  deletedAt: g.deletedAt && BackendDate(g.deletedAt)})) : [],
        res.surchargeOverride,
        res.fixedPrices,
    );
}
